import { useMemo } from 'react'
import { Link } from 'react-router'
import { useTranslation } from 'react-i18next'
import type { ColumnDef } from '@tanstack/react-table'
import { ChevronDown, ChevronRight } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { DataTable } from '@/components/ui/data-table'
import {
  AUCTION_STAGES,
  type AuctionStage,
  type DiagnosticParticipant,
} from '@/services/diagnostics/diagnosticsApi'
import { formatCount } from '@/utils/format'

const STAGE_VARIANTS: Record<AuctionStage, 'success' | 'blue' | 'warning' | 'gray' | 'red'> = {
  WINNING: 'success',
  SELECTED: 'blue',
  // Still eligible to win, only held back this time, so it reads as good news.
  ELIGIBLE: 'success',
  UNSUITABLE: 'red',
  INELIGIBLE: 'red',
  INACTIVE: 'gray',
}

/** A stage heading with its line items beneath it, or one of those line items. */
type Row =
  | { kind: 'stage'; id: string; stage: AuctionStage; subRows: Row[] }
  | { kind: 'lineItem'; id: string; participant: DiagnosticParticipant }

/**
 * A floor diagnostic is either plain text or a JSON object — as an object or
 * as a string holding one — naming the creative, its bid and the floor.
 */
function floorText(entry: unknown): string {
  let parsed = entry
  if (typeof entry === 'string' && entry.startsWith('{')) {
    try {
      parsed = JSON.parse(entry)
    } catch {
      parsed = entry
    }
  }
  if (!parsed || typeof parsed !== 'object') return String(entry ?? '')
  const floor = parsed as Record<string, unknown>
  return [
    floor.creative,
    floor.status,
    floor.bid && `bid ${floor.bid}`,
    floor.resolvedFloor && `floor ${floor.resolvedFloor}`,
  ]
    .filter(Boolean)
    .join(' · ')
}

const negated = (expression: string) => `NOT ${expression.replace(/^NOT\s+/, '')}`

function matchedLines(participant: DiagnosticParticipant): string[] {
  return [...participant.matchedExpressions, ...participant.negatedMatchedExpressions.map(negated)]
}

function unmatchedLines(participant: DiagnosticParticipant): string[] {
  return [
    ...Object.entries(participant.unmatchedExpressionTypes ?? {}).map(
      ([type, count]) => `${type} (×${count})`
    ),
    ...participant.negatedUnmatchedExpressions.map(negated),
    ...Object.entries(participant.unmatchedFilters ?? {}).map(
      ([filter, values]) => `${filter}: ${values.join(', ')}`
    ),
  ]
}

/** Lines in a cell; the table cells do not wrap by themselves. */
function Lines({ lines, label }: { lines: string[]; label?: string }) {
  if (!lines.length) return null
  return (
    <div className="text-xs whitespace-normal">
      {label && <div className="font-medium">{label}</div>}
      {lines.map((line, index) => (
        <div key={index} className="break-words text-muted-foreground">
          {line}
        </div>
      ))}
    </div>
  )
}

export interface DiagnosticParticipantTableProps {
  participants: DiagnosticParticipant[]
  /** Line item names by id, filled in as they load. */
  lineItemNames: Map<string, string>
}

/**
 * Every line item in a diagnostic result, grouped under how far it got in the
 * auction — winners first — with what its targeting did and did not match.
 */
export function DiagnosticParticipantTable({
  participants,
  lineItemNames,
}: DiagnosticParticipantTableProps) {
  const { t } = useTranslation()

  const rows: Row[] = useMemo(
    () =>
      AUCTION_STAGES.map((stage) => ({
        kind: 'stage' as const,
        id: stage,
        stage,
        subRows: participants
          .filter((participant) => participant.reachedStage === stage)
          .map((participant) => ({
            kind: 'lineItem' as const,
            id: String(participant.internalId),
            participant,
          })),
      })).filter((row) => row.subRows.length > 0),
    [participants]
  )

  const columns: ColumnDef<Row, unknown>[] = useMemo(
    () => [
      {
        id: 'lineItem',
        header: t('diagnostics.lineItem'),
        size: 280,
        enableSorting: false,
        cell: ({ row }) => {
          const data = row.original
          if (data.kind === 'stage') {
            return (
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="icon-sm"
                  className="-my-1"
                  aria-label={row.getIsExpanded() ? t('queries.collapse') : t('queries.expand')}
                  onClick={row.getToggleExpandedHandler()}
                >
                  {row.getIsExpanded() ? (
                    <ChevronDown className="size-4" />
                  ) : (
                    <ChevronRight className="size-4" />
                  )}
                </Button>
                <Badge variant={STAGE_VARIANTS[data.stage]}>
                  {t(`diagnostics.stages.${data.stage}`)}
                </Badge>
                <span className="text-xs text-muted-foreground tabular-nums">
                  {formatCount(data.subRows.length)}
                </span>
              </div>
            )
          }
          const { participant } = data
          return (
            <div className="flex flex-col gap-1 pl-9 whitespace-normal">
              <Link to={`/advertising/lineitems/${participant.externalId}`} className="font-medium">
                {lineItemNames.get(participant.externalId) || participant.externalId}
              </Link>
              {participant.hasUnapprovedPoliticalAds && (
                <Badge variant="warning" className="self-start">
                  {t('diagnostics.unapprovedPolitical')}
                </Badge>
              )}
            </div>
          )
        },
      },
      {
        id: 'creatives',
        header: t('diagnostics.creatives'),
        size: 170,
        enableSorting: false,
        cell: ({ row }) => {
          if (row.original.kind !== 'lineItem') return null
          const p = row.original.participant
          const counts = [
            [p.matchedAdCount, 'matched'],
            [p.unmatchedAdCount, 'unmatched'],
            [p.blockedAdCount, 'blocked'],
            [p.inactiveAdCount, 'inactive'],
          ] as const
          return (
            <div className="text-xs whitespace-normal">
              {counts
                .filter(([count]) => count > 0)
                .map(([count, key]) => (
                  <div key={key}>
                    {t(`diagnostics.creativeCount.${key}`, { count: formatCount(count) })}
                  </div>
                ))}
              {p.winningAds.length > 0 && (
                <div className="text-muted-foreground">
                  {t('diagnostics.winningCreatives', { ids: p.winningAds.join(', ') })}
                </div>
              )}
              {p.competingExternalAds.length > 0 && (
                <div className="text-muted-foreground">
                  {t('diagnostics.externalAds', { count: p.competingExternalAds.length })}
                </div>
              )}
            </div>
          )
        },
      },
      {
        id: 'matched',
        header: t('diagnostics.matched'),
        size: 240,
        enableSorting: false,
        cell: ({ row }) =>
          row.original.kind === 'lineItem' && (
            <Lines lines={matchedLines(row.original.participant)} />
          ),
      },
      {
        id: 'unmatched',
        header: t('diagnostics.unmatched'),
        size: 320,
        enableSorting: false,
        cell: ({ row }) =>
          row.original.kind === 'lineItem' && (
            <Lines lines={unmatchedLines(row.original.participant)} />
          ),
      },
      {
        id: 'notes',
        header: t('diagnostics.floorsAndNotes'),
        size: 300,
        enableSorting: false,
        cell: ({ row }) => {
          if (row.original.kind !== 'lineItem') return null
          const p = row.original.participant
          return (
            <div className="flex flex-col gap-1">
              <Lines label={t('diagnostics.lossReasons')} lines={p.lossReasons} />
              <Lines
                label={t('diagnostics.floors')}
                lines={(p.floorDiagnostics ?? []).map(floorText).filter(Boolean)}
              />
              <Lines label={t('diagnostics.blocklist')} lines={p.blocklistDiagnostics} />
            </div>
          )
        },
      },
    ],
    [t, lineItemNames]
  )

  return (
    <DataTable
      columns={columns}
      data={rows}
      getRowId={(row) => row.id}
      getSubRows={(row) => (row.kind === 'stage' ? row.subRows : undefined)}
      // Only what could deliver is open to begin with: the winners, and those
      // that were eligible but held back.
      defaultExpanded={{ WINNING: true, ELIGIBLE: true }}
      emptyMessage={t('diagnostics.noParticipants')}
      inset
    />
  )
}
